import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import SectionCard from "@/components/customers/SectionCard";
import BookMeetingFromRecord from "@/components/salgsmuligheter/BookMeetingFromRecord";
import { useCanBookMeetings } from "@/hooks/use-can-book-meetings";
import { useCustomerMeetings, type CustomerMeeting } from "@/hooks/use-meetings";
import { formatDateTimeShort } from "@/lib/format";
import { MEETING_LOCATION_LABELS, MEETING_STATUS_LABELS, MEETING_STATUS_TONES } from "@/lib/meeting-labels";
import { cn } from "@/lib/utils";
import { TONE_PILL } from "@/lib/widget-status";

interface MeetingsSectionProps {
  customerId: string;
  customerName: string;
}

function MeetingRow({ m, customerId }: { m: CustomerMeeting; customerId: string }) {
  const tone = MEETING_STATUS_TONES[m.status];
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 text-sm">
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{m.title}</span>
        <span className="block text-xs text-muted-foreground">
          {formatDateTimeShort(m.start_at)} · {MEETING_LOCATION_LABELS[m.location_type]}
          {m.host ? ` · vert ${m.host.full_name}` : ""}
          {m.contact ? ` · ${m.contact.full_name}` : ""}
        </span>
      </span>
      <span className={cn("rounded-pill px-2.5 py-0.5 text-xs font-medium", tone ? TONE_PILL[tone] : "bg-muted text-muted-foreground")}>
        {MEETING_STATUS_LABELS[m.status]}
      </span>
      {m.status !== "cancelled" && (
        <Link to={`/kunde/${customerId}/moter/${m.id}`} className="text-xs underline underline-offset-4">
          {m.summary.some((s) => s.status === "approved") ? "Åpne referat" : "Møte og referat"}
        </Link>
      )}
      {m.status === "scheduled" && m.meet_uri && (
        <a href={m.meet_uri} target="_blank" rel="noreferrer" className="text-xs underline underline-offset-4">
          Meet-lenke
        </a>
      )}
      {m.status === "scheduled" && m.html_link && (
        <a href={m.html_link} target="_blank" rel="noreferrer" className="text-xs underline underline-offset-4">
          Åpne i kalender
        </a>
      )}
    </li>
  );
}

function MeetingGroup({ label, meetings, customerId }: { label: string; meetings: CustomerMeeting[]; customerId: string }) {
  if (meetings.length === 0) return null;
  return (
    <div>
      <h3 className="pt-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</h3>
      <ul className="divide-y divide-border">
        {meetings.map((m) => (
          <MeetingRow key={m.id} m={m} customerId={customerId} />
        ))}
      </ul>
    </div>
  );
}

/**
 * «Møter» på kundekortet (CRM-4.4): kommende og tidligere kundemøter, med Book
 * møte for dem som kan booke. Uten møter er kortet én linje.
 */
export default function MeetingsSection({ customerId, customerName }: MeetingsSectionProps) {
  const { data: meetings = [], isLoading } = useCustomerMeetings(customerId);
  const canBook = useCanBookMeetings();
  const [bookOpen, setBookOpen] = useState(false);
  const nowMs = Date.now();
  const upcoming = meetings.filter((m) => m.status === "scheduled" && new Date(m.end_at).getTime() >= nowMs).reverse();
  const previous = meetings.filter((m) => !upcoming.includes(m));

  return (
    <>
      <SectionCard
        compact
        title="Møter"
        meta={upcoming.length > 0 ? `${upcoming.length} kommende` : undefined}
        empty={canBook ? "Ingen møter booket. «Book møte» legger et i kalenderen." : "Ingen møter booket."}
        actions={
          canBook && (
            <Button variant="outline" size="sm" onClick={() => setBookOpen(true)}>
              Book møte
            </Button>
          )
        }
      >
        {isLoading ? (
          <Skeleton className="m-4 h-16" />
        ) : meetings.length === 0 ? null : (
          <div className="px-4 pb-2">
            <MeetingGroup label="Kommende" meetings={upcoming} customerId={customerId} />
            <MeetingGroup label="Tidligere" meetings={previous} customerId={customerId} />
          </div>
        )}
      </SectionCard>
      {canBook && <BookMeetingFromRecord open={bookOpen} onOpenChange={setBookOpen} customerId={customerId} name={customerName} />}
    </>
  );
}
